import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight, Briefcase, Building2, ChevronDown, ClipboardCheck, Clock, Crosshair,
  Hash, MapPin, Maximize2, Navigation, Package, Phone, Wrench, X,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CrewWidgets.css';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from '../../components/projectMapLeaflet';
import { CrewMemberStatus } from '../../interfaces/CrewLiveStatus';
import { StatusPill } from './CrewLiveStatusCard';
import {
  avatarColor, costCodeText, CREW_STAT, CrewSite,
  displayStatus, employeeMarkerPos, escapeHtml, fullName,
  CrewLocMode, groupSites, initials, isFreeTextSO, locEmployees, locLabel, locMode,
  siteHasCoords, wageTypeLabel, workSummary,
} from './crewUtil';
import CrewPunchTable from './CrewPunchTable';

interface CrewMapCardProps {
  crew: CrewMemberStatus[];
  /** Server "asOf" timestamp for the modal subtitle. */
  asOf: string | null;
  /** Employee selected in the crew list (row click → mini map zooms to their site). */
  selectedEmployee: CrewMemberStatus | null;
  /** Increments on every crew-row click so repeat clicks re-trigger the flyTo. */
  focusNonce: number;
  /** Mini-map reset also clears the crew-list row selection. */
  onClearSelection: () => void;
  onViewTimesheet: (member: CrewMemberStatus) => void;
  nowMs: number;
}

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const MINI_SITE_ZOOM = 16;
const BIG_SITE_ZOOM = 17;

type MapCommand =
  | { type: 'fit'; bounds: L.LatLngBounds | null }
  | { type: 'fly'; lat: number; lng: number; zoom: number }
  /** fly-emp: flyTo with the marker placed ~75–80% down the viewport (Fix 4).
   *  Math: project latlng to pixel coords at targetZoom, then subtract 28% of
   *  the container height — this shifts the map CENTER upward in world space,
   *  which moves the marker DOWN the screen, leaving room for the popup above. */
  | { type: 'fly-emp'; lat: number; lng: number; zoom: number };

/** Runs pan/zoom commands inside the Leaflet context (new object per action). */
function MapController({ command }: { command: MapCommand | null }) {
  const map = useMap();
  useEffect(() => {
    if (!command) return;
    if (command.type === 'fit') {
      if (command.bounds) map.fitBounds(command.bounds.pad(0.2), { maxZoom: 15 });
      else map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    } else if (command.type === 'fly-emp') {
      // Project the target coordinate to a pixel point at the target zoom level,
      // then shift it upward by 28% of the map container height. Un-projecting
      // that shifted point gives a new center where the marker will appear ~75-80%
      // down the viewport — room for the popup above the marker.
      const targetZoom = command.zoom;
      const latlng = L.latLng(command.lat, command.lng);
      const pt = map.project(latlng, targetZoom);
      const containerH = map.getContainer().getBoundingClientRect().height || 500;
      pt.y -= containerH * 0.28;
      const newCenter = map.unproject(pt, targetZoom);
      map.flyTo(newCenter, targetZoom, { duration: 0.55 });
    } else {
      map.flyTo([command.lat, command.lng], command.zoom, { duration: 0.55 });
    }
  }, [map, command]);
  return null;
}

function computeBounds(sites: CrewSite[], locEmps?: CrewMemberStatus[]): L.LatLngBounds | null {
  const coords: [number, number][] = sites
    .filter(siteHasCoords)
    .map((s) => [s.latitude, s.longitude]);
  if (locEmps) {
    locEmps.forEach((m) => {
      if (m.gps) coords.push([m.gps.latitude, m.gps.longitude]);
    });
  }
  if (coords.length === 0) return null;
  return L.latLngBounds(coords);
}

/** Teardrop site pin with head count + "{site} · {count}" label chip (per prototype). */
function sitePinIcon(site: CrewSite, sel: boolean, dim: boolean): L.DivIcon {
  const tone = site.anyMeal ? 'tone-warn' : 'tone-full';
  const n = site.employees.length;
  const html =
    `<div class="crew-pin ${tone}${sel ? ' sel' : ''}${dim ? ' dim' : ''}">` +
    `<div class="mp-body"><span class="mp-count">${n}</span></div>` +
    `<div class="mp-label">${escapeHtml(site.name)} · ${n}</div>` +
    '</div>';
  return L.divIcon({ className: 'crew-divicon', html, iconSize: [0, 0], iconAnchor: [0, 0] });
}

/** 36px round avatar employee marker with status ring (expanded map). */
function empMarkerIcon(m: CrewMemberStatus, sel: boolean): L.DivIcon {
  const name = fullName(m);
  const html =
    `<div class="crew-emp-marker${sel ? ' sel' : ''}">` +
    `<span class="em-av" style="background:${avatarColor(name)}">${escapeHtml(initials(name))}` +
    `<span class="em-ring ${CREW_STAT[m.status].dot}"></span></span>` +
    '</div>';
  return L.divIcon({ className: 'crew-divicon', html, iconSize: [0, 0], iconAnchor: [0, 0] });
}

/**
 * Location pin (.loc-pin) divIcon for hourly/service/csw/piece employees without a project.
 * Teardrop avatar (34px) with initials, status ring, and a corner badge:
 *   - service / csw mode → wrench SVG, blue (.svc) badge
 *   - hourly mode        → clock SVG, dark badge
 *   - warehouse mode     → clock SVG, dark badge (kiosk roles; TAS-541)
 *   - piece mode         → package SVG, dark badge
 * Under-label chip: "{first name} · {Warranty|Service|Warehouse|Hourly|Piece}"
 * Raw SVG paths used instead of lucide components (divIcon is a plain string).
 */
function locPinIcon(m: CrewMemberStatus, sel: boolean): L.DivIcon {
  const name = fullName(m);
  const mode = locMode(m);
  const firstName = escapeHtml(m.name.first);
  // Wrench path: ic-wrench from the prototype SVG symbols
  const wrenchPath = 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5z';
  // Clock path: circle + hands
  const clockPath = 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-14v4l3 2';
  // Package path (box/parcel)
  const packagePath = 'M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12';

  let badgePath: string;
  let badgeClass: string;
  let modeLabel: string;
  if (mode === 'csw') {
    badgePath = wrenchPath;
    badgeClass = 'lp-badge svc';
    modeLabel = m.customerServiceWarranty?.warrantyProject ? 'Warranty' : 'Service';
  } else if (mode === 'warehouse') {
    badgePath = clockPath;
    badgeClass = 'lp-badge';
    modeLabel = 'Warehouse';
  } else if (mode === 'piece') {
    badgePath = packagePath;
    badgeClass = 'lp-badge';
    modeLabel = 'Piece';
  } else if (mode === 'service') {
    badgePath = wrenchPath;
    badgeClass = 'lp-badge svc';
    modeLabel = 'Service';
  } else {
    badgePath = clockPath;
    badgeClass = 'lp-badge';
    modeLabel = 'Hourly';
  }

  const html =
    `<div class="crew-loc-pin${sel ? ' sel' : ''}">` +
    `<span class="lp-av" style="background:${avatarColor(name)}">` +
    `<span class="lp-init">${escapeHtml(initials(name))}</span>` +
    `<span class="${badgeClass}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${badgePath}"/></svg></span>` +
    `<span class="em-ring ${CREW_STAT[m.status].dot}"></span>` +
    '</span>' +
    `<span class="lp-label">${firstName} · ${modeLabel}</span>` +
    '</div>';
  return L.divIcon({ className: 'crew-divicon', html, iconSize: [0, 0], iconAnchor: [0, 0] });
}

const CrewMapCard: React.FC<CrewMapCardProps> = ({
  crew, asOf, selectedEmployee, focusNonce, onClearSelection, onViewTimesheet, nowMs,
}) => {
  const sites = useMemo(() => groupSites(crew), [crew]);
  // Location-based: on-clock employees with no project
  const locEmps = useMemo(() => locEmployees(crew), [crew]);
  const activeN = crew.filter((c) => c.status !== 'out').length;
  const locN = locEmps.length;

  // Marker position per employee: actual punch GPS, or a small ring around the
  // site coordinate when gps is null (fallback per the handoff).
  const empPositions = useMemo(() => {
    const map = new Map<number, [number, number]>();
    sites.forEach((site) => {
      const noGps = site.employees.filter((e) => !e.gps);
      site.employees.forEach((e) => {
        const pos = employeeMarkerPos(e, site, noGps.indexOf(e), noGps.length);
        if (pos) map.set(e.employeeID, pos);
      });
    });
    return map;
  }, [sites]);

  // ── Mini map state ─────────────────────────────────────────────────────────
  const [miniSelSiteID, setMiniSelSiteID] = useState<number | null>(null);
  const [miniCmd, setMiniCmd] = useState<MapCommand | null>(null);

  // Refit when the set of mappable sites/loc-employees changes (initial load / crew change).
  const sitesSig = [
    ...sites.filter(siteHasCoords).map((s) => `s${s.projectID}`),
    ...locEmps.filter((m) => m.gps).map((m) => `l${m.employeeID}`),
  ].sort().join(',');
  useEffect(() => {
    setMiniCmd({ type: 'fit', bounds: computeBounds(sites, locEmps) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitesSig]);

  // Crew-row click → zoom the mini map to that employee's site or GPS and
  // select the pin; clocked-out rows (or missing coords) reset to fit-bounds.
  // For location-based employees, fly to their own punch GPS.
  useEffect(() => {
    if (focusNonce === 0) return;
    const emp = selectedEmployee;
    if (emp && emp.status !== 'out') {
      const proj = emp.project;
      if (proj && typeof proj.latitude === 'number' && typeof proj.longitude === 'number') {
        // Job-based: zoom to project site
        setMiniSelSiteID(proj.projectID);
        setMiniCmd({ type: 'fly', lat: proj.latitude, lng: proj.longitude, zoom: MINI_SITE_ZOOM });
        return;
      }
      if (!proj && emp.gps) {
        // Location-based with GPS: zoom to their exact punch location
        setMiniSelSiteID(null);
        setMiniCmd({ type: 'fly', lat: emp.gps.latitude, lng: emp.gps.longitude, zoom: MINI_SITE_ZOOM });
        return;
      }
    }
    // Clocked-out or no coords → reset
    setMiniSelSiteID(null);
    setMiniCmd({ type: 'fit', bounds: computeBounds(sites, locEmps) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  const miniZoomSite = (site: CrewSite) => {
    if (!siteHasCoords(site)) return;
    setMiniSelSiteID(site.projectID);
    setMiniCmd({ type: 'fly', lat: site.latitude, lng: site.longitude, zoom: MINI_SITE_ZOOM });
  };

  const miniReset = () => {
    setMiniSelSiteID(null);
    setMiniCmd({ type: 'fit', bounds: computeBounds(sites, locEmps) });
    onClearSelection();
  };

  // ── Expanded modal state ───────────────────────────────────────────────────
  const [expanded, setExpanded] = useState(false);
  const [activeSiteID, setActiveSiteID] = useState<number | null>(null);
  const [popup, setPopup] = useState<
    { kind: 'site'; siteID: number } | { kind: 'emp'; employeeID: number } | null
  >(null);
  const [bigCmd, setBigCmd] = useState<MapCommand | null>(null);
  // Fix 2: expansion state for punch-table carets in the modal's employee list.
  const [sideExpandedIDs, setSideExpandedIDs] = useState<Set<number>>(new Set());
  const toggleSideExpanded = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setSideExpandedIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const blockRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // loc-employees expansion in the side list
  const [locExpandedIDs, setLocExpandedIDs] = useState<Set<number>>(new Set());
  const toggleLocExpanded = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setLocExpandedIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openExpanded = () => {
    setActiveSiteID(null);
    setPopup(null);
    setBigCmd({ type: 'fit', bounds: computeBounds(sites, locEmps) });
    setExpanded(true);
  };

  const closeExpanded = () => {
    setExpanded(false);
    setActiveSiteID(null);
    setPopup(null);
    setBigCmd(null);
    setSideExpandedIDs(new Set());
    setLocExpandedIDs(new Set());
  };

  // Esc closes the expanded modal.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeExpanded(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Keep the active site's block visible in the side list.
  useEffect(() => {
    if (activeSiteID == null) return;
    blockRefs.current[activeSiteID]?.scrollIntoView({ block: 'nearest' });
  }, [activeSiteID]);

  const bigZoomSite = (site: CrewSite) => {
    setActiveSiteID(site.projectID);
    if (siteHasCoords(site)) {
      setBigCmd({ type: 'fly', lat: site.latitude, lng: site.longitude, zoom: BIG_SITE_ZOOM });
      setPopup({ kind: 'site', siteID: site.projectID });
    } else {
      setPopup(null); // coordinate-less site: highlight the block only
    }
  };

  const bigZoomEmp = (m: CrewMemberStatus) => {
    if (m.status === 'out') return;
    if (!m.project) {
      // Location-based: fly to the employee's own GPS
      if (m.gps) {
        setBigCmd({ type: 'fly-emp', lat: m.gps.latitude, lng: m.gps.longitude, zoom: BIG_SITE_ZOOM });
        setPopup({ kind: 'emp', employeeID: m.employeeID });
      }
      return;
    }
    const site = sites.find((s) => s.projectID === m.project?.projectID);
    if (!site) return;
    setActiveSiteID(site.projectID);
    const pos = empPositions.get(m.employeeID);
    if (pos) {
      // Fix 4: fly-emp offsets center so the marker lands ~75-80% down the viewport
      setBigCmd({ type: 'fly-emp', lat: pos[0], lng: pos[1], zoom: BIG_SITE_ZOOM });
      setPopup({ kind: 'emp', employeeID: m.employeeID });
    } else if (siteHasCoords(site)) {
      setBigCmd({ type: 'fly', lat: site.latitude, lng: site.longitude, zoom: BIG_SITE_ZOOM });
      setPopup(null);
    }
  };

  const bigReset = () => {
    setActiveSiteID(null);
    setPopup(null);
    setBigCmd({ type: 'fit', bounds: computeBounds(sites, locEmps) });
  };

  // Fix 3: popup punch disclosure removed; no popupPunchOpen state needed.

  // Resolve popup targets against the CURRENT crew (poll may replace the data).
  const popupSite = popup?.kind === 'site' ? sites.find((s) => s.projectID === popup.siteID) : undefined;
  const popupEmp = popup?.kind === 'emp' ? crew.find((c) => c.employeeID === popup.employeeID) : undefined;
  // For location-based employees the popup anchors to their own GPS, not empPositions (site-ring).
  const popupEmpPos: [number, number] | undefined = popupEmp
    ? (locMode(popupEmp) !== null && popupEmp.gps
        ? [popupEmp.gps.latitude, popupEmp.gps.longitude]
        : empPositions.get(popupEmp.employeeID))
    : undefined;

  const activeSite = activeSiteID != null ? sites.find((s) => s.projectID === activeSiteID) : undefined;

  const asOfText = asOf
    ? new Date(asOf).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;

  const legend = (
    <div className="map-legend">
      <span className="lg"><span className="cs-dot dot-in" />On the clock</span>
      <span className="lg"><span className="cs-dot dot-meal" />Meal break</span>
      <span className="lg"><span className="lg-loc" />On location</span>
    </div>
  );

  // This card hides Leaflet's own attribution control and renders it here instead,
  // so the credit has to track MAP_TILE_ATTRIBUTION. That constant is a build-time
  // literal (never user input), hence the safe innerHTML.
  const attribution = (
    <div className="map-attr">
      <span dangerouslySetInnerHTML={{ __html: MAP_TILE_ATTRIBUTION }} />
      {' '}· GPS at last punch
    </div>
  );

  // Fix 3: popup keeps status/site/code/wage/since/phone/View-timesheet; punch disclosure removed.
  // For location-based employees the project row is replaced with the navigation label;
  // the code row shows mode-specific detail.
  const empPopupContent = (m: CrewMemberStatus) => {
    const name = fullName(m);
    const wage = wageTypeLabel(m.wageType);
    const ws = workSummary(m, nowMs);
    const mode = locMode(m);
    const isLoc = mode !== null;
    const label = isLoc ? locLabel(m) : null;
    const csw = m.customerServiceWarranty;
    // Sub-detail for location popup varies by mode
    const locDetail = isLoc
      ? (mode === 'csw' && csw
          ? (csw.name || csw.description
              ? `${csw.name || ''}${csw.description ? ` · ${csw.description}` : ''}`
              : null)
          : mode === 'service' && m.serviceOrder?.name
            ? m.serviceOrder.name
            : mode === 'hourly' && m.accountCode
              ? `${m.accountCode.altAccountCodeID} · ${m.accountCode.name}`
              : null)
      : null;
    // CSW warranty project second detail line
    const locDetail2 = mode === 'csw' && csw?.warrantyProject
      ? `${csw.warrantyProject.name} · ${csw.warrantyProject.altProjectID}`
      : null;
    return (
      <>
        <div className="mpop-head">
          <span className="crew-av mpop-av" style={{ background: avatarColor(name) }}>{initials(name)}</span>
          <div>
            <div className="mpop-name">{name}</div>
            <div className="mpop-role">{m.jobTitle || '—'}</div>
          </div>
          <button className="mpop-x" type="button" onClick={() => setPopup(null)}><X /></button>
        </div>
        <div className="mpop-body mpop-body-scroll">
          <StatusPill status={displayStatus(m)} />
          {isLoc ? (
            <>
              {/* Location row: navigation icon + label */}
              <div className="mpop-row"><Navigation /><span><b>{label}</b></span></div>
              {/* Sub-detail: mode-specific */}
              {locDetail && <div className="mpop-row"><Hash /><span className="mono">{locDetail}</span></div>}
              {locDetail2 && <div className="mpop-row"><Hash /><span className="mono">{locDetail2}</span></div>}
            </>
          ) : (
            <>
              <div className="mpop-row"><MapPin /><span>{m.project?.name || '—'}</span></div>
              <div className="mpop-row"><Hash /><span className="mono">{costCodeText(m)}</span></div>
            </>
          )}
          {wage && <div className="mpop-row"><Briefcase /><span>{wage}</span></div>}
          {ws && (
            <div className="mpop-row">
              <Clock />
              <span>Since {ws.since} · <b>{ws.worked}</b></span>
            </div>
          )}
          {m.phoneNumber && <div className="mpop-row"><Phone /><span>{m.phoneNumber}</span></div>}
        </div>
        <div className="mpop-foot">
          <button className="btn sm" type="button" onClick={() => onViewTimesheet(m)}>
            <ClipboardCheck />View timesheet
          </button>
        </div>
      </>
    );
  };

  const sitePopupContent = (site: CrewSite) => (
    <>
      <div className="mpop-head">
        <span className="mpop-si"><Building2 /></span>
        <div>
          <div className="mpop-name">{site.name}</div>
          <div className="mpop-role">{site.altProjectID} · {site.employees.length} on site</div>
        </div>
        <button className="mpop-x" type="button" onClick={() => setPopup(null)}><X /></button>
      </div>
      <div className="mpop-body">
        {site.address && <div className="mpop-row"><MapPin /><span>{site.address}</span></div>}
        <div className="mpop-emps">
          {site.employees.map((m) => {
            const name = fullName(m);
            return (
              <div className="mpop-emp" key={m.employeeID} onClick={() => bigZoomEmp(m)}>
                <span className="crew-av mpop-emp-av" style={{ background: avatarColor(name) }}>{initials(name)}</span>
                <span className="mpe-name">{name}</span>
                <span className={`pill mpe-pill ${CREW_STAT[m.status].pill}`}>{CREW_STAT[m.status].label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div className="card crew-map-card">
      <div className="card-head">
        <MapPin />
        <h3>Crew Map</h3>
        <span className="right">
          <button className="btn ghost sm" type="button" onClick={openExpanded}>
            <Maximize2 />Expand
          </button>
        </span>
      </div>
      <div className="crew-map-pad">
        <div className="crew-map">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            className="crew-map-inner"
            zoomControl={false}
            attributionControl={false}
            scrollWheelZoom={false}
          >
            <TileLayer url={MAP_TILE_URL} maxZoom={MAP_TILE_MAX_ZOOM} referrerPolicy="strict-origin-when-cross-origin" />
            <MapController command={miniCmd} />
            {sites.filter(siteHasCoords).map((site) => (
              <Marker
                key={site.projectID}
                position={[site.latitude as number, site.longitude as number]}
                icon={sitePinIcon(site, miniSelSiteID === site.projectID, false)}
                eventHandlers={{ click: () => miniZoomSite(site) }}
              />
            ))}
            {/* Loc-pin markers: always visible on mini map at employee's GPS */}
            {locEmps.filter((m) => m.gps).map((m) => (
              <Marker
                key={`loc-${m.employeeID}`}
                position={[m.gps!.latitude, m.gps!.longitude]}
                icon={locPinIcon(m, selectedEmployee?.employeeID === m.employeeID && !selectedEmployee?.project)}
                eventHandlers={{ click: () => {
                  if (m.gps) {
                    setMiniSelSiteID(null);
                    setMiniCmd({ type: 'fly', lat: m.gps.latitude, lng: m.gps.longitude, zoom: MINI_SITE_ZOOM });
                  }
                }}}
                zIndexOffset={500}
              />
            ))}
          </MapContainer>
          <div className="map-toolbar">
            <button className="mtb" type="button" title="Expand map" onClick={openExpanded}><Maximize2 /></button>
            <button className="mtb" type="button" title="Reset zoom" onClick={miniReset}><Crosshair /></button>
          </div>
          {legend}
          {attribution}
        </div>
        <div className="crew-map-foot">
          <span className="cmf-count">
            {activeN} active across <b>{sites.length} {sites.length === 1 ? 'site' : 'sites'}</b>
            {locN > 0 && <> · <b>{locN} on location</b></>}
          </span>
          <button className="linkbtn" type="button" onClick={openExpanded}>
            Open full map <ArrowRight />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="crew-map-modal-bg"
          onClick={(e) => { if (e.target === e.currentTarget) closeExpanded(); }}
        >
          <div className="crew-map-modal">
            <div className="modal-head">
              <span className="mi"><MapPin /></span>
              <div>
                <h3>Crew Map — Live Locations</h3>
                <div className="ms">
                  Where your crew is clocked in right now{asOfText ? ` · updated ${asOfText}` : ''}
                </div>
              </div>
              <button className="x" type="button" onClick={closeExpanded}><X /></button>
            </div>
            <div className="map-modal-grid">
              <div className="mm-map">
                <div className="crew-map big">
                  <MapContainer
                    center={DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    className="crew-map-inner"
                    zoomControl={false}
                    attributionControl={false}
                    scrollWheelZoom={true}
                  >
                    <TileLayer url={MAP_TILE_URL} maxZoom={MAP_TILE_MAX_ZOOM} referrerPolicy="strict-origin-when-cross-origin" />
                    <MapController command={bigCmd} />
                    {sites.filter(siteHasCoords).map((site) => (
                      <Marker
                        key={site.projectID}
                        position={[site.latitude as number, site.longitude as number]}
                        icon={sitePinIcon(site, false, activeSiteID === site.projectID)}
                        eventHandlers={{ click: () => bigZoomSite(site) }}
                      />
                    ))}
                    {activeSite && activeSite.employees.map((m) => {
                      const pos = empPositions.get(m.employeeID);
                      if (!pos) return null;
                      return (
                        <Marker
                          key={m.employeeID}
                          position={pos}
                          icon={empMarkerIcon(m, popupEmp?.employeeID === m.employeeID)}
                          zIndexOffset={1000}
                          eventHandlers={{ click: () => bigZoomEmp(m) }}
                        />
                      );
                    })}
                    {/* Loc-pin markers: always visible on big map, not gated by activeSite */}
                    {locEmps.filter((m) => m.gps).map((m) => (
                      <Marker
                        key={`loc-${m.employeeID}`}
                        position={[m.gps!.latitude, m.gps!.longitude]}
                        icon={locPinIcon(m, popupEmp?.employeeID === m.employeeID)}
                        zIndexOffset={800}
                        eventHandlers={{ click: () => bigZoomEmp(m) }}
                      />
                    ))}
                    {popupSite && siteHasCoords(popupSite) && (
                      <Popup
                        key={`site-${popupSite.projectID}`}
                        position={[popupSite.latitude as number, popupSite.longitude as number]}
                        className="crew-mpop"
                        closeButton={false}
                        closeOnClick={false}
                        autoClose={false}
                        closeOnEscapeKey={false}
                        offset={[0, -60]}
                      >
                        {sitePopupContent(popupSite)}
                      </Popup>
                    )}
                    {popupEmp && popupEmpPos && (
                      <Popup
                        key={`emp-${popupEmp.employeeID}`}
                        position={popupEmpPos}
                        className="crew-mpop"
                        closeButton={false}
                        closeOnClick={false}
                        autoClose={false}
                        closeOnEscapeKey={false}
                        autoPan={false}
                        offset={[0, -26]}
                      >
                        {empPopupContent(popupEmp)}
                      </Popup>
                    )}
                  </MapContainer>
                  <div className="map-toolbar">
                    <button className="mtb" type="button" title="Reset view" onClick={bigReset}><Crosshair /></button>
                  </div>
                  {legend}
                  {attribution}
                </div>
              </div>
              <div className="mm-side">
                {sites.length === 0 && locEmps.length === 0 ? (
                  <div className="crew-empty">
                    <MapPin />
                    <div className="et">No one on the clock</div>
                    <p>Job sites appear here while crew members are clocked in.</p>
                  </div>
                ) : (
                  sites.map((site) => (
                    <div
                      key={site.projectID}
                      className={`site-block${activeSiteID === site.projectID ? ' active' : ''}`}
                      ref={(el) => { blockRefs.current[site.projectID] = el; }}
                    >
                      <div className="site-head" onClick={() => bigZoomSite(site)}>
                        <span className="si"><Building2 /></span>
                        <div>
                          <div className="st-name">{site.name}</div>
                          <div className="st-addr">
                            {site.altProjectID}{site.address ? ` · ${site.address}` : ''}
                          </div>
                        </div>
                        <span className="st-ct">{site.employees.length} on site</span>
                      </div>
                      {site.employees.map((m) => {
                        const name = fullName(m);
                        const sideHasPunches = m.punches.length > 0;
                        const sideExpanded = sideHasPunches && sideExpandedIDs.has(m.employeeID);
                        const sideWs = workSummary(m, nowMs);
                        return (
                          // Fix 2: site-emp row gets caret + expandable punch table
                          <div
                            key={m.employeeID}
                            className={`site-emp-item${sideExpanded ? ' open' : ''}`}
                          >
                            <div className="site-emp" onClick={() => bigZoomEmp(m)}>
                              <span className="crew-av site-emp-av" style={{ background: avatarColor(name) }}>
                                {initials(name)}
                              </span>
                              <div>
                                <div className="se-name">{name}</div>
                                <div className="se-code">{costCodeText(m)}</div>
                              </div>
                              <div className="se-r">
                                <StatusPill status={displayStatus(m)} />
                                {sideWs && (
                                  <div className="cst-time">
                                    since {sideWs.since} · <b>{sideWs.worked}</b>
                                  </div>
                                )}
                              </div>
                              {sideHasPunches ? (
                                <button
                                  className="crew-exp side-emp-exp"
                                  type="button"
                                  title="View today's punches"
                                  onClick={(e) => toggleSideExpanded(e, m.employeeID)}
                                  aria-expanded={sideExpanded}
                                >
                                  <span className="ce-tx">Punches</span>
                                  <ChevronDown className="ic" />
                                </button>
                              ) : (
                                <span className="crew-exp-sp" />
                              )}
                            </div>
                            {sideHasPunches && sideExpanded && (
                              <div className="site-emp-punches">
                                <CrewPunchTable
                                  punches={m.punches}
                                  status={m.status}
                                  nowMs={nowMs}
                                  kioskUser={m.kioskUser}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
                {/* Location-based employees, split per mode: CSW → Service Orders
                    → Piece Work → Hourly (non job-time). Rows mirror site-emp rows
                    (pill, since/worked, Punches caret) with the location label + mode
                    icon in the code line; click zooms to the employee's own GPS. */}
                {([
                  ['csw', Wrench, 'Customer Service & Warranty', 'On location · customer service & warranty'],
                  ['service', Wrench, 'Service Orders', 'On location · service call'],
                  ['warehouse', Clock, 'Warehouse', 'Kiosk · warehouse & drivers (hourly)'],
                  ['piece', Package, 'Piece Work', 'On location · piece work'],
                  ['hourly', Clock, 'Hourly (non job-time)', 'On location · not on a job'],
                ] as Array<[CrewLocMode, React.FC<{ className?: string }>, string, string]>)
                  .map(([mode, Icon, title, sub]) => {
                    const list = locEmps.filter((m) => locMode(m) === mode);
                    if (list.length === 0) return null;
                    return (
                      <div key={mode} className="site-block">
                        <div className="site-head" style={{ cursor: 'default' }}>
                          <span className="si"><Icon /></span>
                          <div>
                            <div className="st-name">{title}</div>
                            <div className="st-addr">{sub}</div>
                          </div>
                          <span className="st-ct">{list.length}</span>
                        </div>
                        {list.map((m) => {
                          const name = fullName(m);
                          const locHasPunches = m.punches.length > 0;
                          const locExpanded = locHasPunches && locExpandedIDs.has(m.employeeID);
                          const locWs = workSummary(m, nowMs);
                          const csw = m.customerServiceWarranty;
                          // Code line: CSW shows locationName; others show locLabel
                          const codeLabel = mode === 'csw' && csw
                            ? (csw.locationName || csw.name || 'Service')
                            : locLabel(m) || '';
                          // CSW rows: mirror the My Crew card's sub lines below the
                          // location — "{CSW name} · {description}" + warranty project.
                          const cswSub = mode === 'csw' && csw && (csw.name || csw.description)
                            ? `${csw.name || ''}${csw.description ? `${csw.name ? ' · ' : ''}${csw.description}` : ''}`
                            : null;
                          const cswWarranty = mode === 'csw' && csw?.warrantyProject
                            ? `${csw.warrantyProject.name} · ${csw.warrantyProject.altProjectID}`
                            : null;
                          // NPM free-text SO rows: label is "Service Order" so the
                          // text itself renders below (mirrors the cswSub pattern).
                          const soSub = mode === 'service' && isFreeTextSO(m.serviceOrder)
                            ? m.serviceOrder!.name
                            : null;
                          return (
                            <div
                              key={m.employeeID}
                              className={`site-emp-item${locExpanded ? ' open' : ''}`}
                            >
                              <div className="site-emp" onClick={() => bigZoomEmp(m)}>
                                <span className="crew-av site-emp-av" style={{ background: avatarColor(name) }}>
                                  {initials(name)}
                                </span>
                                <div>
                                  <div className="se-name">{name}</div>
                                  <div className="se-code"><Icon />{codeLabel}</div>
                                  {cswSub && <div className="se-sub">{cswSub}</div>}
                                  {cswWarranty && <div className="se-sub se-sub-mono">{cswWarranty}</div>}
                                  {soSub && <div className="se-sub">{soSub}</div>}
                                </div>
                                <div className="se-r">
                                  <StatusPill status={displayStatus(m)} />
                                  {locWs && (
                                    <div className="cst-time">
                                      since {locWs.since} · <b>{locWs.worked}</b>
                                    </div>
                                  )}
                                </div>
                                {locHasPunches ? (
                                  <button
                                    className="crew-exp side-emp-exp"
                                    type="button"
                                    title="View today's punches"
                                    onClick={(e) => toggleLocExpanded(e, m.employeeID)}
                                    aria-expanded={locExpanded}
                                  >
                                    <span className="ce-tx">Punches</span>
                                    <ChevronDown className="ic" />
                                  </button>
                                ) : (
                                  <span className="crew-exp-sp" />
                                )}
                              </div>
                              {locHasPunches && locExpanded && (
                                <div className="site-emp-punches">
                                  <CrewPunchTable
                                    punches={m.punches}
                                    status={m.status}
                                    nowMs={nowMs}
                                    kioskUser={m.kioskUser}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewMapCard;
